import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError, API, authParam } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import {
  ArrowLeft, Scale, Sparkles, Save, Download, CheckCircle2, AlertTriangle, Loader2,
  FileText, Building2, ScrollText, History, Pencil, Eye, ShieldAlert, ListChecks, GitFork,
} from "lucide-react";

const SECTIONS = [
  { key: "subject", label: "Subject" },
  { key: "background_facts", label: "Background Facts" },
  { key: "policy_position", label: "Policy-Based Position" },
  { key: "legal_arguments", label: "Legal Position (Indian Consumer Law)" },
  { key: "procedural_unfairness", label: "Procedural Fairness" },
  { key: "reconsideration_request", label: "Request for Reconsideration" },
  { key: "reserved_rights_escalation", label: "Reserved Rights & Further Steps" },
  { key: "signature_block", label: "Signature" },
  { key: "annexures", label: "Annexures" },
];

const ESC_OPTIONS = [
  { key: "include_insurer_grievance", label: "Insurer Grievance Officer (GRO)" },
  { key: "include_irdai", label: "IRDAI / Bima Bharosa (IGMS)" },
  { key: "include_ombudsman", label: "Insurance Ombudsman" },
  { key: "include_consumer_commission", label: "Consumer Commission" },
];

const MERIT = {
  challengeable: { label: "Challengeable", cls: "bg-success/10 text-success border-success/30" },
  ambiguous: { label: "Ambiguous", cls: "bg-warning/10 text-warning border-warning/30" },
  substantially_valid: { label: "Substantially Valid", cls: "bg-destructive/10 text-destructive border-destructive/30" },
};

function Chip({ children }) {
  return <li className="rounded-md bg-secondary px-3 py-2 text-sm text-primary/90">{children}</li>;
}

function ColHeader({ Icon, title }) {
  return (
    <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-white/90 px-4 py-3 backdrop-blur">
      <Icon className="h-4 w-4 text-accent" />
      <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">{title}</h3>
    </div>
  );
}

function DraftPanel({ sections, setSections, onSave, saving, caseId, hasDraft }) {
  const [mode, setMode] = useState("preview");
  if (!hasDraft) return null;
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-border bg-white/90 px-4 py-2.5 backdrop-blur">
        <div className="inline-flex rounded-md border border-border p-0.5">
          <button onClick={() => setMode("preview")} data-testid="draft-preview-mode" className={`flex items-center gap-1.5 rounded px-3 py-1 text-xs font-medium transition-colors duration-200 ${mode === "preview" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}><Eye className="h-3.5 w-3.5" />Preview</button>
          <button onClick={() => setMode("edit")} data-testid="draft-edit-mode" className={`flex items-center gap-1.5 rounded px-3 py-1 text-xs font-medium transition-colors duration-200 ${mode === "edit" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}><Pencil className="h-3.5 w-3.5" />Edit</button>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={onSave} disabled={saving} data-testid="save-draft-btn" className="gap-1.5">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}Save
          </Button>
          <a href={`${API}/cases/${caseId}/export?${authParam()}`} data-testid="export-word-btn">
            <Button size="sm" className="gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90"><Download className="h-3.5 w-3.5" />Export Word</Button>
          </a>
        </div>
      </div>

      <div className="scroll-col flex-1 bg-muted/30 p-6">
        {mode === "edit" ? (
          <div className="space-y-4">
            {SECTIONS.map((s) => (
              <div key={s.key} className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{s.label}</Label>
                <Textarea data-testid={`draft-section-${s.key}`} rows={s.key === "subject" ? 2 : 4}
                  value={sections[s.key] || ""} onChange={(e) => setSections({ ...sections, [s.key]: e.target.value })}
                  className="font-mono text-xs" />
              </div>
            ))}
          </div>
        ) : (
          <div data-testid="draft-preview" className="paper mx-auto max-w-[800px] rounded-sm p-10 text-sm leading-relaxed text-slate-800">
            <p className="text-center text-xs font-semibold tracking-widest text-accent">WITHOUT PREJUDICE</p>
            {sections.subject && <p className="mt-6 font-semibold text-primary">Subject: {sections.subject}</p>}
            {SECTIONS.filter((s) => s.key !== "subject").map((s) => sections[s.key] ? (
              <div key={s.key} className="mt-5">
                <h4 className="font-heading text-base font-semibold text-primary">{s.label}</h4>
                {String(sections[s.key]).split("\n").map((p, i) => p.trim() && <p key={i} className="mt-2 whitespace-pre-wrap">{p}</p>)}
              </div>
            ) : null)}
          </div>
        )}
      </div>
    </div>
  );
}

function EscalationDialog({ open, setOpen, cfg, setCfg, onGenerate, generating, label }) {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="open-generate-btn" className="gap-2 bg-accent text-accent-foreground hover:bg-accent/90">
          <Sparkles className="h-4 w-4" />{label}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Escalation references</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Choose which forums the letter may reference as further steps if unresolved. Language stays controlled and non-committal.</p>
        <div className="space-y-3 py-2">
          {ESC_OPTIONS.map((o) => (
            <label key={o.key} className="flex cursor-pointer items-center gap-3 rounded-md border border-border p-3 hover:border-accent/40">
              <Checkbox checked={!!cfg[o.key]} onCheckedChange={(v) => setCfg({ ...cfg, [o.key]: !!v })} data-testid={`esc-${o.key}`} />
              <span className="text-sm text-primary">{o.label}</span>
            </label>
          ))}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Reviewer note (optional)</Label>
            <Textarea rows={2} value={cfg.custom_notes || ""} onChange={(e) => setCfg({ ...cfg, custom_notes: e.target.value })} data-testid="esc-custom-notes" placeholder="e.g. confirm insurer's published grievance email before issue" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onGenerate} disabled={generating} data-testid="generate-draft-btn" className="w-full gap-2 bg-accent text-accent-foreground hover:bg-accent/90">
            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {generating ? "Drafting letter…" : "Generate draft"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CaseReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refreshSubscription } = useAuth();
  const isStaff = user?.role === "consultant" || user?.role === "admin";

  const [caseData, setCaseData] = useState(null);
  const [docs, setDocs] = useState([]);
  const [versions, setVersions] = useState([]);
  const [sections, setSections] = useState({});
  const [escCfg, setEscCfg] = useState({});
  const [analyzing, setAnalyzing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [escOpen, setEscOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, d, v] = await Promise.all([
        api.get(`/cases/${id}`), api.get(`/cases/${id}/documents`), api.get(`/cases/${id}/versions`),
      ]);
      setCaseData(c.data);
      setDocs(d.data);
      setVersions(v.data);
      setSections(c.data.current_draft || {});
      setEscCfg(c.data.escalation_config || {});
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      navigate("/");
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const analyze = async () => {
    setAnalyzing(true);
    try {
      await api.post(`/cases/${id}/analyze`);
      toast.success("Analysis complete");
      await load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setAnalyzing(false); }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/cases/${id}/generate-draft`, { escalation: escCfg });
      setSections(data.sections);
      setEscOpen(false);
      toast.success("Draft generated — please review before issue");
      refreshSubscription();
      await load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setGenerating(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/cases/${id}/draft`, { sections, note: "Reviewer edit" });
      toast.success("Draft saved");
      await load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const setStatus = async (status) => {
    try {
      await api.patch(`/cases/${id}/status`, { status });
      toast.success(status === "finalized" ? "Case finalized" : "Status updated");
      await load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  if (!caseData) return <Layout><div className="space-y-4"><Skeleton className="h-10 w-72" /><Skeleton className="h-96 w-full" /></div></Layout>;

  const a = caseData.analysis;
  const hasDraft = !!caseData.current_draft;
  const warnings = [...(a?.review_warnings || [])];
  const merit = a && (MERIT[a.merit_assessment] || null);

  return (
    <Layout>
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <button onClick={() => navigate("/")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary" data-testid="review-back-btn">
            <ArrowLeft className="h-4 w-4" /> Dashboard
          </button>
          <h1 className="mt-2 font-heading text-3xl font-bold text-primary">{caseData.title}</h1>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {caseData.insurer_name && <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{caseData.insurer_name}</span>}
            {caseData.policy_number && <span className="font-mono">Policy {caseData.policy_number}</span>}
            {caseData.claim_number && <span className="font-mono">Claim {caseData.claim_number}</span>}
            {a?.case_type && <Badge variant="outline" className="capitalize">{a.case_type.replace("_", " ")}</Badge>}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={caseData.status} />
          {versions.length > 0 && (
            <Dialog>
              <DialogTrigger asChild><Button variant="outline" size="sm" className="gap-1.5" data-testid="versions-btn"><History className="h-4 w-4" />Versions ({versions.length})</Button></DialogTrigger>
              <DialogContent className="max-h-[80vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-heading">Draft version history</DialogTitle></DialogHeader>
                <div className="space-y-2">
                  {versions.map((v, i) => (
                    <button key={v.id} data-testid={`version-${i}`} onClick={() => { setSections(v.sections); toast.info("Loaded version into editor — Save to keep it"); }}
                      className="w-full rounded-md border border-border p-3 text-left text-sm hover:border-accent/40">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-primary">{i === 0 ? "Latest" : `Revision ${versions.length - i}`} · {v.note}</span>
                        <GitFork className="h-3.5 w-3.5 text-muted-foreground" />
                      </div>
                      <div className="mt-0.5 text-xs text-muted-foreground">{v.author} · {new Date(v.created_at).toLocaleString()}</div>
                    </button>
                  ))}
                </div>
              </DialogContent>
            </Dialog>
          )}
          {caseData.status !== "finalized" && hasDraft && (
            <Button size="sm" onClick={() => setStatus("finalized")} data-testid="finalize-btn" className="gap-1.5 bg-success text-success-foreground hover:bg-success/90"><CheckCircle2 className="h-4 w-4" />Finalize</Button>
          )}
        </div>
      </div>

      {/* No analysis yet */}
      {!a && (
        <Card className="mt-8 flex flex-col items-center gap-3 rounded-md border-dashed p-12 text-center">
          <Scale className="h-10 w-10 text-accent" />
          <h2 className="font-heading text-2xl font-semibold text-primary">Analyze the documents</h2>
          <p className="max-w-md text-sm text-muted-foreground">{docs.length ? `${docs.length} document(s) uploaded. Run the evidence analysis to extract facts, insurer grounds and gaps.` : "Upload documents from the new-case flow first."}</p>
          <Button onClick={analyze} disabled={analyzing || !docs.length} data-testid="run-analysis-btn" className="mt-2 gap-2 bg-accent text-accent-foreground hover:bg-accent/90">
            {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Scale className="h-4 w-4" />}{analyzing ? "Analyzing…" : "Run analysis"}
          </Button>
        </Card>
      )}

      {/* Warnings banner */}
      {a && (warnings.length > 0 || a.document_quality !== "strong") && (
        <Card data-testid="warnings-banner" className="mt-6 rounded-md border-warning/40 bg-warning/10 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-primary">Review carefully before issue</span>
                <Badge variant="outline" className="border-warning/40 capitalize text-warning">Documents: {a.document_quality}</Badge>
                <Badge variant="outline" className="capitalize">Confidence: {a.confidence}</Badge>
                {merit && <Badge variant="outline" className={`capitalize ${merit.cls}`}>{merit.label}</Badge>}
              </div>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-primary/80">
                {warnings.slice(0, 6).map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          </div>
        </Card>
      )}

      {/* Main workspace */}
      {a && (
        isStaff ? (
          <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-12" style={{ height: "calc(100vh - 300px)", minHeight: 480 }}>
            {/* Facts */}
            <Card className="flex flex-col overflow-hidden rounded-md lg:col-span-3">
              <ColHeader Icon={FileText} title="Extracted Facts" />
              <div className="scroll-col flex-1 space-y-4 p-4">
                {a.coverage_summary && <div><p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Coverage</p><p className="text-sm text-primary/90">{a.coverage_summary}</p></div>}
                <div><p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Claim Facts</p><ul className="space-y-1.5">{(a.claim_facts || []).map((f, i) => <Chip key={i}>{f}</Chip>)}</ul></div>
                {(a.policy_references || []).length > 0 && <div><p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Policy References</p><ul className="space-y-1.5">{a.policy_references.map((r, i) => <li key={i} className="rounded-md border border-border p-2 text-sm"><span className="font-semibold text-accent">{r.clause}</span><p className="text-primary/80">{r.text}</p></li>)}</ul></div>}
                {(a.contradictions || []).length > 0 && <div><p className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase text-destructive"><ShieldAlert className="h-3.5 w-3.5" />Contradictions</p><ul className="space-y-1.5">{a.contradictions.map((c, i) => <li key={i} className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-primary/90">{c}</li>)}</ul></div>}
              </div>
            </Card>

            {/* Grounds */}
            <Card className="flex flex-col overflow-hidden rounded-md lg:col-span-3">
              <ColHeader Icon={ScrollText} title="Insurer Grounds" />
              <div className="scroll-col flex-1 space-y-4 p-4">
                {a.settlement_gap && <div className="rounded-md bg-secondary p-3"><p className="text-xs font-semibold uppercase text-muted-foreground">Settlement Gap</p><p className="text-sm text-primary/90">{a.settlement_gap}</p></div>}
                <div><p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Stated Grounds</p><ul className="space-y-1.5">{(a.insurer_grounds || []).map((g, i) => <Chip key={i}>{g}</Chip>)}</ul></div>
                <div><p className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase text-warning"><ListChecks className="h-3.5 w-3.5" />Missing Info Checklist</p><ul className="space-y-1.5">{(a.missing_info || []).map((m, i) => <li key={i} className="flex gap-2 rounded-md border border-warning/30 bg-warning/5 px-3 py-2 text-sm text-primary/90"><span className="text-warning">•</span>{m}</li>)}</ul></div>
                {a.recommended_strategy && <div><p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Recommended Strategy</p><p className="rounded-md bg-primary/5 p-3 text-sm text-primary/90">{a.recommended_strategy}</p></div>}
              </div>
            </Card>

            {/* Draft */}
            <Card className="flex flex-col overflow-hidden rounded-md lg:col-span-6">
              {!hasDraft ? (
                <>
                  <ColHeader Icon={Sparkles} title="Generated Draft" />
                  <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
                    <Sparkles className="h-8 w-8 text-accent" />
                    <p className="max-w-sm text-sm text-muted-foreground">Generate a fact-bound challenge letter from the analysis. Pick escalation forums first.</p>
                    <EscalationDialog open={escOpen} setOpen={setEscOpen} cfg={escCfg} setCfg={setEscCfg} onGenerate={generate} generating={generating} label="Generate draft" />
                  </div>
                </>
              ) : (
                <DraftPanel sections={sections} setSections={setSections} onSave={save} saving={saving} caseId={id} hasDraft={hasDraft} />
              )}
            </Card>
          </div>
        ) : (
          /* Consumer guided single column */
          <div className="mx-auto mt-6 max-w-3xl space-y-5">
            <Accordion type="single" collapsible className="rounded-md border border-border bg-white px-4">
              <AccordionItem value="facts" className="border-b-0">
                <AccordionTrigger data-testid="consumer-summary-toggle" className="text-sm font-semibold text-primary">What we found in your documents</AccordionTrigger>
                <AccordionContent className="space-y-4 pb-4">
                  {a.coverage_summary && <div><p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Coverage</p><p className="text-sm text-primary/90">{a.coverage_summary}</p></div>}
                  <div><p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Insurer's reasons</p><ul className="space-y-1.5">{(a.insurer_grounds || []).map((g, i) => <Chip key={i}>{g}</Chip>)}</ul></div>
                  <div><p className="mb-1 text-xs font-semibold uppercase text-warning">What we still need</p><ul className="space-y-1.5">{(a.missing_info || []).map((m, i) => <li key={i} className="rounded-md border border-warning/30 bg-warning/5 px-3 py-2 text-sm">{m}</li>)}</ul></div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            {!hasDraft ? (
              <Card className="flex flex-col items-center gap-3 rounded-md p-10 text-center">
                <Sparkles className="h-8 w-8 text-accent" />
                <h2 className="font-heading text-xl font-semibold text-primary">Generate your letter</h2>
                <p className="max-w-sm text-sm text-muted-foreground">We'll prepare a fact-based letter you can review and download. It's always marked for review before sending.</p>
                <EscalationDialog open={escOpen} setOpen={setEscOpen} cfg={escCfg} setCfg={setEscCfg} onGenerate={generate} generating={generating} label="Generate my letter" />
              </Card>
            ) : (
              <Card className="flex h-[70vh] flex-col overflow-hidden rounded-md">
                <DraftPanel sections={sections} setSections={setSections} onSave={save} saving={saving} caseId={id} hasDraft={hasDraft} />
              </Card>
            )}
          </div>
        )
      )}
    </Layout>
  );
}
